import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Banknote, Workflow, Boxes, ShieldCheck, Zap } from "lucide-react";
import CTAButtons from "@/components/CTAButtons";
import TrustBar from "@/components/TrustBar";

function AppCard({ app }) {
    const sources = [
        // 1) Clearbit — full-quality colour brand marks (when reachable)
        `https://logo.clearbit.com/${app.domain}?size=200`,
        // 2) Google s2 favicons — extremely reliable fallback (128 px PNG)
        `https://www.google.com/s2/favicons?domain=${app.domain}&sz=128`,
    ];
    const [srcIndex, setSrcIndex] = useState(0);
    const [errored, setErrored] = useState(false);
    const initials = app.name
        .split(/\s+/)
        .map((w) => w[0])
        .join("")
        .slice(0, 2)
        .toUpperCase();

    const handleError = () => {
        if (srcIndex < sources.length - 1) {
            setSrcIndex((i) => i + 1);
        } else {
            setErrored(true);
        }
    };

    return (
        <a
            href={app.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`app-${app.name.toLowerCase().replace(/\s/g, "-")}`}
            className="bg-white p-6 flex flex-col items-start gap-3 lift group no-underline"
        >
            <div className="w-14 h-14 bg-white border border-tca-line flex items-center justify-center overflow-hidden">
                {errored ? (
                    <span className="font-display font-extrabold text-tca-orange text-sm tracking-tight">{initials}</span>
                ) : (
                    <img
                        key={sources[srcIndex]}
                        src={sources[srcIndex]}
                        alt={`${app.name} logo`}
                        className="max-w-[44px] max-h-[44px] object-contain"
                        loading="lazy"
                        decoding="async"
                        referrerPolicy="no-referrer"
                        onError={handleError}
                    />
                )}
            </div>
            <div>
                <p className="font-display font-bold text-tca-ink group-hover:text-tca-orange transition-colors">{app.name}</p>
                <p className="text-xs text-tca-mute">{app.category}</p>
            </div>
        </a>
    );
}

const XERO_GOLD =
    "/assets/xero-gold-partner-banner.jpg";

function XeroBrandMark() {
    const [errored, setErrored] = useState(false);
    if (errored) {
        return (
            <span className="font-display font-extrabold text-[#13B5EA] text-2xl tracking-tight">xero</span>
        );
    }
    return (
        <img
            src="https://logo.clearbit.com/xero.com"
            alt="Xero"
            className="h-8 w-auto"
            onError={() => setErrored(true)}
        />
    );
}

const FEATURED_APPS = [
    { name: "Hubdoc", domain: "hubdoc.com", category: "Bills & receipts", url: "https://www.hubdoc.com" },
    { name: "Dext", domain: "dext.com", category: "Receipts & invoices", url: "https://dext.com" },
    { name: "PayStack", domain: "paystack.com", category: "Payments", url: "https://paystack.com" },
    { name: "Make.com", domain: "make.com", category: "Automations", url: "https://www.make.com" },
    { name: "Yoco", domain: "yoco.com", category: "Card terminals", url: "https://www.yoco.com" },
    { name: "Shopify", domain: "shopify.com", category: "E-commerce", url: "https://www.shopify.com" },
    { name: "WooCommerce", domain: "woocommerce.com", category: "E-commerce", url: "https://woocommerce.com" },
    { name: "SimplePay", domain: "simplepay.co.za", category: "SA Payroll", url: "https://www.simplepay.co.za" },
    { name: "Unleashed", domain: "unleashedsoftware.com", category: "Inventory", url: "https://www.unleashedsoftware.com" },
    { name: "DEAR Cin7", domain: "cin7.com", category: "Inventory & POS", url: "https://www.cin7.com" },
    { name: "Chaser", domain: "chaserhq.com", category: "Debtor follow-ups", url: "https://www.chaserhq.com" },
    { name: "HikePOS", domain: "hikeup.com", category: "Point of Sale", url: "https://www.hikeup.com" },
    { name: "WhatsApp", domain: "whatsapp.com", category: "Communication", url: "https://www.whatsapp.com" },
    { name: "Zapier", domain: "zapier.com", category: "Automations", url: "https://zapier.com" },
    { name: "PaySpace", domain: "payspace.com", category: "SA Payroll", url: "https://www.payspace.com" },
    { name: "Syft Analytics", domain: "syftanalytics.com", category: "Reporting & Analytics", url: "https://www.syftanalytics.com" },
];

const BENEFITS = [
    {
        icon: Workflow,
        title: "Live bank feeds",
        text: "Automatic feeds from FNB, Standard Bank, ABSA, Nedbank, Capitec, Investec and Discovery Bank — your books update overnight.",
    },
    {
        icon: ShieldCheck,
        title: "SARS-aligned",
        text: "Direct VAT201 export, EMP201 reconciliation and IT14 mapping — Xero is built to talk to SARS eFiling.",
    },
    {
        icon: Boxes,
        title: "1,000+ App Store integrations",
        text: "Whatever your business runs on — Shopify, Yoco, SimplePay, HubSpot — Xero has a verified plug-in.",
    },
    {
        icon: Zap,
        title: "AI automations",
        text: "We layer Zapier and ChatGPT on top of Xero to automate repeat work — invoicing, debtor chasing, and report generation.",
    },
];

export default function Software() {
    return (
        <div data-testid="page-software">
            {/* Hero */}
            <section className="bg-white border-b border-tca-line relative overflow-hidden">
                <div className="absolute inset-0 grid-bg opacity-60" />
                <div className="container-tca relative py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-11 items-center">
                    <div className="lg:col-span-5 animate-fade-up">
                        <div className="flex items-center gap-3 mb-5">
                            <XeroBrandMark />
                            <span className="overline text-tca-orange">Xero Gold Partner · Established 2015</span>
                        </div>
                        <h1 className="font-display font-extrabold text-tca-ink text-5xl sm:text-6xl lg:text-7xl leading-[0.98]">
                            Cloud accounting,
                            <span className="block text-tca-orange">done properly.</span>
                        </h1>
                        <p className="mt-6 text-tca-mute text-lg leading-relaxed max-w-xl">
                            TC Auditors is an active Xero Gold Partner. Every TCA client runs on Xero — with bank feeds, live management reports and a tailored stack from the Xero App Store. Real-time business visibility, IFRS-aligned reporting, and zero stress at year-end.
                        </p>
                        <CTAButtons className="mt-9" testIdPrefix="software-hero-cta" inquiryLabel="Talk to a Xero Specialist" />
                    </div>
                    <div className="lg:col-span-7">
                        <div className="border border-tca-line bg-white p-2">
                            <img
                                src={XERO_GOLD}
                                alt="Xero Gold Partner — TC Auditors"
                                className="w-full h-auto"
                                loading="eager"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Why Xero */}
            <section className="bg-white">
                <div className="container-tca py-20 md:py-28">
                    <div className="max-w-2xl mb-14">
                        <p className="overline mb-3">Why Xero</p>
                        <h2 className="font-display font-extrabold text-tca-ink text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
                            The world-leading cloud accounting platform — and why we made it our default.
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-tca-line border border-tca-line">
                        {BENEFITS.map((b, i) => {
                            const Icon = b.icon;
                            const isAlt = i % 2 !== 0;
                            return (
                                <div key={b.title} data-testid={`benefit-${b.title.toLowerCase().replace(/\s/g, "-")}`}
                                    className={`p-7 lift group transition-colors duration-200 ${isAlt ? "bg-orange-100" : "bg-white"} hover:bg-tca-orange`}>
                                    <Icon className="w-7 h-7 text-tca-orange group-hover:text-white mb-5 transition-colors duration-200" />
                                    <h3 className="font-display font-bold text-lg text-tca-ink group-hover:text-white mb-2 transition-colors duration-200">{b.title}</h3>
                                    <p className="text-sm text-tca-mute group-hover:text-white/80 leading-relaxed transition-colors duration-200">{b.text}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Xero in motion (small embedded video) */}
            <section className="bg-tca-fog border-y border-tca-line">
                <div className="container-tca py-20 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-5">
                        <p className="overline mb-3">See Xero in motion</p>
                        <h2 className="font-display font-extrabold text-tca-ink text-3xl sm:text-4xl leading-[1.05]">
                            What Xero looks like, end-to-end.
                        </h2>
                        <p className="mt-5 text-tca-mute leading-relaxed">
                            A short product overview from Xero. We layer our own training, monthly oversight and tailored Xero App Store integrations on top.
                        </p>
                        <ul className="mt-6 space-y-2 text-sm">
                            {[
                                "Live VAT, PAYE & cash-flow dashboards",
                                "Bank feeds for every major SA bank",
                                "Mobile + desktop, anywhere",
                                "ISO 27001-certified security",
                            ].map((t) => (
                                <li key={t} className="flex items-center gap-2 text-tca-ink">
                                    <Check className="w-4 h-4 text-tca-orange shrink-0" /> {t}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="lg:col-span-7">
                        <div className="aspect-video w-full border border-tca-line bg-tca-ink overflow-hidden" data-testid="xero-video">
                            <iframe
                                width="100%"
                                height="100%"
                                src="https://www.youtube.com/embed/6sjDwyOLNNY?start=39&rel=0"
                                title="Xero — Beautiful business"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Compare Xero to Sage and QuickBooks */}
            <section className="bg-white border-t border-tca-line" data-testid="software-compare-section">
                <div className="container-tca py-20 md:py-24">
                    <div className="max-w-3xl mb-10">
                        <p className="overline mb-3 text-tca-orange">Competitive Analysis</p>
                        <h2 className="font-display font-extrabold text-tca-ink text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
                            Compare Xero to Sage and QuickBooks
                        </h2>
                        <div className="mt-6 space-y-3 text-tca-mute leading-relaxed">
                            <p>
                                After years of migrating South African businesses from Sage and QuickBooks to Xero, these are the seven advantages that matter most to our clients:
                            </p>
                            <ul className="space-y-3 mt-4">
                                {[
                                    {
                                        title: "Xero App Store — 1,000+ certified integrations",
                                        body: "Xero's marketplace is more than five times larger than Sage's (~200 apps) and significantly ahead of QuickBooks (~750 apps), giving you a genuinely future-proof platform that connects to whatever tools your business runs on — Shopify, Yoco, SimplePay, HubSpot and hundreds more.",
                                    },
                                    {
                                        title: "Find & Recode — a feature with no equivalent elsewhere",
                                        body: "Xero's Find & Recode lets you bulk-search transactions using up to nine conditions and recode multiple fields simultaneously — fixing coding errors, wrong tax rates or mismatched contacts across hundreds of entries in minutes. Neither Sage nor QuickBooks offers anything close.",
                                    },
                                    {
                                        title: "AI-powered Analytics with 180-day cash flow forecasting",
                                        body: "Xero Analytics Plus (built on Syft, now part of Xero) delivers AI-driven insights, KPI dashboards and a 180-day cash flow forecast with scenario modelling — helping you plan for hiring, debt repayment or a slow season before it arrives. No equivalent is available in Sage or QuickBooks.",
                                    },
                                    {
                                        title: "Direct VAT201 eFiling to SARS — from inside Xero",
                                        body: "Xero connects directly to SARS eFiling and auto-populates your VAT201 return from your transactions. You review and submit — no export, no re-keying. Sage and QuickBooks both require you to manually export data and re-enter it into eFiling.",
                                    },
                                    {
                                        title: "Unlimited users — every plan, no per-seat charges",
                                        body: "Every Xero plan supports unlimited users at no extra cost. Sage and QuickBooks both gate user numbers behind plan tiers, meaning a growing team quickly pushes you into a more expensive tier.",
                                    },
                                    {
                                        title: "Hubdoc included free on all plans",
                                        body: "Hubdoc — a powerful document-capture tool that auto-codes bills and receipts from email or photo — is included free with every Xero subscription. Sage and QuickBooks users must pay for a third-party equivalent (Dext or AutoEntry).",
                                    },
                                    {
                                        title: "The deepest certified-advisor network in South Africa",
                                        body: "Xero's Gold and Platinum Partner programme has built the strongest ecosystem of qualified local accountants and bookkeepers in South Africa. When something goes wrong — or when you need a migration done properly — that network matters.",
                                    },
                                ].map((item, i) => (
                                    <li key={i} className="flex items-start gap-3 text-sm text-tca-ink leading-relaxed">
                                        <span className="shrink-0 w-6 h-6 bg-tca-orange text-white text-[11px] font-extrabold flex items-center justify-center mt-0.5">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        <span>
                                            <strong className="text-tca-ink">{item.title}. </strong>
                                            {item.body}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    <Link
                        to="/software/xero-comparison"
                        data-testid="xero-comparison-link"
                        className="btn-primary mt-4 gap-2 inline-flex"
                    >
                        See full comparison of Xero vs Sage vs QuickBooks <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>

            {/* Xero App Store grid */}
            <section className="bg-white">
                <div className="container-tca py-20 md:py-28">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
                        <div className="max-w-2xl">
                            <p className="overline mb-3 text-tca-orange">Xero App Store</p>
                            <h2 className="font-display font-extrabold text-tca-ink text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
                                1,000+ integrations. Sixteen we love.
                            </h2>
                            <p className="mt-4 text-tca-mute">
                                The right app stack pays for itself in hours saved. We help every client choose, install and maintain the integrations that actually matter.
                            </p>
                        </div>
                        <a
                            href="https://apps.xero.com/za"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="xero-app-store-link"
                            className="btn-ghost"
                        >
                            Browse the Xero App Store <ArrowRight className="w-4 h-4" />
                        </a>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-tca-line border border-tca-line">
                        {FEATURED_APPS.map((a) => (
                            <AppCard key={a.name} app={a} />
                        ))}
                    </div>
                </div>
            </section>

            <TrustBar />

            {/* Migration CTA */}
            <section className="bg-tca-ink text-white relative overflow-hidden">
                <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-tca-orange opacity-20 blur-3xl" />
                <div className="container-tca relative py-20 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-8">
                        <Banknote className="w-9 h-9 text-tca-orange mb-5" />
                        <h2 className="font-display font-extrabold text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
                            Ready to migrate from Pastel, Sage or QuickBooks to Xero?
                        </h2>
                        <p className="mt-5 text-white/70 max-w-2xl leading-relaxed">
                            We do an average of two migrations a month. Trial balance import at conversion date, master file migration, bank feed activation and three years of comparatives — typically completed within 2–4 weeks.
                        </p>
                    </div>
                    <div className="lg:col-span-4 flex flex-col gap-3">
                        <CTAButtons testIdPrefix="software-cta" inquiryLabel="Plan my Xero Migration" />
                    </div>
                </div>
            </section>
        </div>
    );
}
